/**
 * Preview token schemas (issue #208) — shared between the API (server-side
 * validation of the `POST /api/admin/preview/token` body) and the web SPA
 * (response typing for the `useCreatePreviewToken` hook).
 *
 * Why short-lived URL tokens instead of cookies:
 *   The auth-gated preview routes (`/preview/page/:id`,
 *   `/preview/content-version/:id`) live on the public host
 *   (`<site>.<parent>`), but the admin session cookie is scoped to the
 *   admin host. Browsers don't send the cookie to a sibling subdomain
 *   without a wide `Domain` attribute — and the platform's mixed
 *   deployment model (subdomain customers + custom-domain customers)
 *   means no single cookie scope works universally. We carry a
 *   short-lived signed JWT in the URL `?t=` query param instead. Tokens
 *   never reach localStorage / IndexedDB — they're minted server-side
 *   per click and expire on their own.
 *
 * The token TTL is enforced server-side (see
 * `api/src/auth/previewToken.ts`); this schema only carries the request
 * + response shapes.
 */

import { z } from "zod";

/**
 * Discriminator for which kind of resource the token authorises preview
 * access to. Matches the two preview routes in
 * `api/src/public-renderer/renderer.routes.ts`.
 */
export const previewResourceType = z.enum(["page-version", "content-version"]);
export type PreviewResourceType = z.infer<typeof previewResourceType>;

/**
 * Request body for `POST /api/admin/preview/token`. The `resourceId` is a
 * thin string check — the actual existence / site-ownership guard lives
 * in the service layer (mirroring what the existing preview routes
 * already do via the cross-site leak guard).
 */
export const previewTokenRequest = z.object({
  resourceType: previewResourceType,
  resourceId: z.string().min(1),
});
export type PreviewTokenRequest = z.infer<typeof previewTokenRequest>;

/**
 * Response shape: the signed JWT plus its absolute expiry (ISO string).
 * The expiry lets the SPA window.open immediately without re-checking;
 * the API still enforces the same expiry server-side at verify time.
 */
export const previewTokenResponse = z.object({
  token: z.string().min(1),
  expiresAt: z.string().min(1),
});
export type PreviewTokenResponse = z.infer<typeof previewTokenResponse>;
